import {DeliveryRunsControls} from "./AdminDeliveryRuns";
import { useEffect, useState, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Clock, Truck, AlertTriangle, CheckCircle2, RefreshCw, Navigation, User, MapPin, RotateCcw, Package, MessageSquare, RotateCw, UserX, CheckCheck, Bell, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("ayrshire_token");
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

function pence(p: number) {
  return `£${(p / 100).toFixed(2)}`;
}

/** Inline "up the driver fee" control shown on order cards. Sets the TOTAL the
 *  driver is offered (delivery fee + bonus). The bonus/fuel line is SHOP-funded
 *  (it shows on the shop's Finance Centre as "Driver fuel/bonus you funded"), so
 *  raising the fee increases what the shop pays the driver — not a hub cost.
 *  The quick +50p / +£1 buttons ADD to the current total; the box SETS it. */
function BoostFeeControl({
  orderId, deliveryFeePence, fuelSupplementPence, onSaved, notify,
}: {
  orderId: number;
  deliveryFeePence: number | null;
  fuelSupplementPence: number | null;
  onSaved: () => void;
  notify: (t: { title: string; description?: string; variant?: "destructive" }) => void;
}) {
  // Fuel/bonus line defaults to £1.00 in the dispatch engine when unset.
  const currentTotalPence = (deliveryFeePence ?? 0) + (fuelSupplementPence ?? 100);
  const [value, setValue] = useState<string>((currentTotalPence / 100).toFixed(2));
  const [saving, setSaving] = useState(false);

  // Single POST path shared by the quick-add buttons and the Set box, so the
  // total that lands in the DB is always exactly what the admin intended.
  const postTotal = async (totalPence: number) => {
    if (!Number.isFinite(totalPence) || totalPence < 0 || totalPence > 5000) {
      notify({ title: "Driver fee must be between £0 and £50", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/orders/${orderId}/driver-fee`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ totalPence }),
      });
      const d = await res.json().catch(() => ({})) as { driverFeePence?: number; reoffered?: boolean; error?: string };
      if (!res.ok) { notify({ title: d.error ?? "Could not update fee", variant: "destructive" }); return; }
      setValue(((d.driverFeePence ?? totalPence) / 100).toFixed(2));
      notify({
        title: `Driver fee set to ${pence(d.driverFeePence ?? totalPence)}`,
        description: d.reoffered ? "Re-offered to drivers at the new fee." : "Assigned driver's pay updated (shop-funded).",
      });
      onSaved();
    } catch {
      notify({ title: "Network error updating fee", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const save = () => void postTotal(Math.round(Number(value) * 100));
  // ADD to the current saved total — "+50p" means +50p, never a reset.
  const bump = (deltaPence: number) => void postTotal(currentTotalPence + deltaPence);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-semibold text-slate-500">Driver fee (total)</span>
      <button onClick={() => bump(50)} disabled={saving}
        className="px-2 py-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 disabled:opacity-50 text-emerald-800 text-xs font-bold transition-colors">
        +50p
      </button>
      <button onClick={() => bump(100)} disabled={saving}
        className="px-2 py-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 disabled:opacity-50 text-emerald-800 text-xs font-bold transition-colors">
        +£1
      </button>
      <div className="flex items-center rounded-xl border border-emerald-200 bg-emerald-50 overflow-hidden">
        <span className="pl-2 pr-1 text-emerald-700 text-sm font-semibold">£</span>
        <input
          type="number" min={0} max={50} step={0.5} value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-16 bg-transparent py-1.5 text-sm text-emerald-800 font-semibold outline-none"
        />
        <button
          onClick={save} disabled={saving}
          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold transition-colors"
        >
          {saving ? "…" : "Set"}
        </button>
      </div>
      <span className="text-[11px] text-slate-400">now {pence(currentTotalPence)} · shop-funded</span>
    </div>
  );
}

function ago(iso: string) {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}

function timeLeft(expiresAt: string) {
  const secs = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
  if (secs === 0) return "expired";
  return `${secs}s`;
}

type Offer = {
  driverName: string | null;
  status: string;
  offeredAt: string;
  expiresAt: string;
  respondedAt: string | null;
  distanceKm: number | null;
};

type DispatchingOrder = {
  orderId: number;
  storeName: string | null;
  status: string;
  dispatchStatus: string | null;
  deliveryAddress: string | null;
  customerName: string | null;
  totalPence: number;
  deliveryFeePence: number | null;
  driverFuelSupplementPence: number | null;
  createdAt: string;
  offers: Offer[];
};

type InTransitOrder = {
  orderId: number;
  storeName: string | null;
  status: string;
  deliveryAddress: string | null;
  customerName: string | null;
  totalPence: number;
  deliveryFeePence: number | null;
  driverFuelSupplementPence: number | null;
  createdAt: string;
  driverName: string | null;
  claimedAt: string | null;
  pickedUpAt: string | null;
  driverLat: number | null;
  driverLng: number | null;
};

type FailedOrder = {
  orderId: number;
  storeName: string | null;
  status: string;
  deliveryAddress: string | null;
  customerName: string | null;
  totalPence: number;
  deliveryFeePence: number | null;
  driverFuelSupplementPence: number | null;
  createdAt: string;
};

type LiveData = {
  dispatching: DispatchingOrder[];
  inTransit: InTransitOrder[];
  failures: FailedOrder[];
};

type ItemDeliveryRequest = {
  id: number;
  status: string;
  customerName: string;
  pickupAddress: string;
  dropoffAddress: string;
  distanceMiles: number;
  quotedFeePence: number;
  driverEarningsPence: number;
  platformFeePence: number;
  tipPence: number;
  grossTipPence?:number;
  rating: number | null;
  driverName: string | null;
  createdAt: string;
};

type ChatMessage = {
  id: number;
  senderId: number;
  senderType: string;
  content: string;
  createdAt: string;
};

const OFFER_STATUS_STYLE: Record<string, string> = {
  pending:  "bg-amber-50 dark:bg-amber-900/20 text-amber-700 border-amber-200",
  accepted: "bg-green-50 text-green-700 border-green-200",
  declined: "bg-red-50 text-red-700 border-red-200",
  expired:  "bg-slate-100 text-slate-500 border-slate-200",
  cancelled:"bg-slate-100 text-slate-500 border-slate-200",
};

const ITEM_STATUS_LABELS: Record<string, string> = {
  quoted: "Quoted", dispatching: "Dispatching", no_drivers: "No Drivers",
  accepted: "Accepted", picked_up: "Picked Up", delivered: "Delivered", cancelled: "Cancelled",
  refunded: "Refunded",
};

const ITEM_STATUS_STYLE: Record<string, { badge: string; border: string }> = {
  quoted:      { badge: "bg-slate-100 text-slate-600",   border: "border-slate-200" },
  dispatching: { badge: "bg-amber-100 text-amber-700",   border: "border-amber-200" },
  no_drivers:  { badge: "bg-red-100 text-red-700",       border: "border-red-200" },
  accepted:    { badge: "bg-blue-100 text-blue-700",     border: "border-blue-200" },
  picked_up:   { badge: "bg-blue-100 text-blue-700",     border: "border-blue-200" },
  delivered:   { badge: "bg-green-100 text-green-700",   border: "border-green-200" },
  cancelled:   { badge: "bg-slate-100 text-slate-500",   border: "border-slate-200" },
  refunded:    { badge: "bg-red-100 text-red-600",       border: "border-red-200" },
};

export default function AdminHubDash() {
  const { toast } = useToast();
  const [data, setData] = useState<LiveData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [redispatchingId, setRedispatchingId] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  const [itemDeliveries, setItemDeliveries] = useState<ItemDeliveryRequest[]>([]);
  const [itemDeliveriesLoading, setItemDeliveriesLoading] = useState(true);
  const [refundingId, setRefundingId] = useState<number | null>(null);
  const [itemRedispatchingId, setItemRedispatchingId] = useState<number | null>(null);
  const [messagesModal, setMessagesModal] = useState<{ requestId: number; messages: ChatMessage[] } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/hub-dash/live", { headers: authHeaders() });
      if (res.ok) {
        const d = await res.json() as LiveData;
        setData(d);
        setLastRefresh(new Date());
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const loadItemDeliveries = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/item-delivery-requests?limit=50", { headers: authHeaders() });
      if (res.ok) {
        const d = await res.json() as { requests: ItemDeliveryRequest[] };
        setItemDeliveries(d.requests);
      }
    } catch { /* ignore */ }
    finally { setItemDeliveriesLoading(false); }
  }, []);

  // Auto-refresh every 8 seconds
  useEffect(() => { void load(); void loadItemDeliveries(); }, [load, loadItemDeliveries]);
  useEffect(() => {
    const interval = setInterval(() => { void load(); void loadItemDeliveries(); }, 8000);
    return () => clearInterval(interval);
  }, [load, loadItemDeliveries]);

  // Tick every second so timers update live
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);
  void tick; // suppress unused warning

  const handleRedispatch = async (orderId: number) => {
    if (!confirm(`Re-dispatch order #${orderId} to nearby drivers?`)) return;
    setRedispatchingId(orderId);
    try {
      const res = await fetch(`/api/admin/orders/${orderId}/redispatch`, {
        method: "POST", headers: authHeaders(),
      });
      const d = await res.json() as { error?: string };
      if (!res.ok) { toast({ title: d.error ?? "Failed", variant: "destructive" }); return; }
      toast({ title: `Order #${orderId} re-dispatched` });
      void load();
    } catch { toast({ title: "Network error", variant: "destructive" }); }
    finally { setRedispatchingId(null); }
  };

  const handleItemRefund = async (id: number, customerName: string, amount: number) => {
    if (!confirm(`Refund ${pence(amount)} to ${customerName} for delivery #${id}? Completed driver earnings are preserved.`)) return;
    setRefundingId(id);
    try {
      const res = await fetch(`/api/admin/item-delivery/${id}/refund`, {
        method: "POST", headers: authHeaders(),
      });
      const d = await res.json() as { error?: string;pending?:boolean };
      if (!res.ok) { toast({ title: d.error ?? "Refund failed", variant: "destructive" }); return; }
      toast({ title: d.pending?`Delivery #${id} refund is pending`:`Delivery #${id} refund confirmed` });
      void loadItemDeliveries();
    } catch { toast({ title: "Network error", variant: "destructive" }); }
    finally { setRefundingId(null); }
  };

  /* Take a job back off the driver holding it. The force-reassign endpoint has
     existed all along but no button ever called it, so once a delivery reached
     "accepted" an admin had no way to move it — which is how #8 sat with one
     driver for 33 minutes while the customer waited. */
  /* Close a job the driver finished but never marked done — the exact situation
     that previously required editing the database by hand. */
  const handleItemComplete = async (id: number) => {
    if (!confirm(`Mark delivery #${id} as delivered?`)) return;
    setItemRedispatchingId(id);
    try {
      const res = await fetch(`/api/admin/item-delivery/${id}/force-complete`, {
        method: "POST", headers: authHeaders(),
      });
      const d = await res.json() as { success?: boolean; error?: string };
      if (!res.ok || !d.success) { toast({ title: d.error ?? "Could not complete", variant: "destructive" }); return; }
      toast({ title: `Delivery #${id} marked delivered` });
      void loadItemDeliveries();
    } catch { toast({ title: "Network error", variant: "destructive" }); }
    finally { setItemRedispatchingId(null); }
  };

  const handleItemRemind = async (id: number) => {
    try {
      const res = await fetch(`/api/admin/item-delivery/${id}/remind-driver`, {
        method: "POST", headers: authHeaders(),
      });
      const d = await res.json() as { success?: boolean; error?: string };
      toast(res.ok && d.success
        ? { title: `Reminder sent to the driver for #${id}` }
        : { title: d.error ?? "Could not send reminder", variant: "destructive" });
    } catch { toast({ title: "Network error", variant: "destructive" }); }
  };

  const handleItemReassign = async (id: number) => {
    if (!confirm(`Take delivery #${id} off its current driver and re-offer it to other drivers?`)) return;
    setItemRedispatchingId(id);
    try {
      const res = await fetch(`/api/admin/item-delivery/${id}/force-reassign`, {
        method: "POST", headers: authHeaders(),
      });
      const d = await res.json() as { success?: boolean; error?: string };
      if (!res.ok || !d.success) { toast({ title: d.error ?? "Reassign failed", variant: "destructive" }); return; }
      toast({ title: `Delivery #${id} taken off its driver and re-offered` });
      void loadItemDeliveries();
    } catch { toast({ title: "Network error", variant: "destructive" }); }
    finally { setItemRedispatchingId(null); }
  };

  const handleItemRedispatch = async (id: number) => {
    if (!confirm(`Re-dispatch delivery #${id} to nearby drivers?`)) return;
    setItemRedispatchingId(id);
    try {
      const res = await fetch(`/api/admin/item-delivery/${id}/redispatch`, {
        method: "POST", headers: authHeaders(),
      });
      const d = await res.json() as { error?: string };
      if (!res.ok) { toast({ title: d.error ?? "Re-dispatch failed", variant: "destructive" }); return; }
      toast({ title: `Delivery #${id} re-dispatched` });
      void loadItemDeliveries();
    } catch { toast({ title: "Network error", variant: "destructive" }); }
    finally { setItemRedispatchingId(null); }
  };

  const loadMessages = async (requestId: number) => {
    try {
      const res = await fetch(`/api/admin/item-delivery/${requestId}/messages`, { headers: authHeaders() });
      if (res.ok) {
        const d = await res.json() as { messages: ChatMessage[] };
        setMessagesModal({ requestId, messages: d.messages });
      }
    } catch { toast({ title: "Failed to load messages", variant: "destructive" }); }
  };

  const total = (data?.dispatching.length ?? 0) + (data?.inTransit.length ?? 0) + (data?.failures.length ?? 0);

  return (
    <AdminLayout current="/admin/hub-dash">
      {/* Messages modal */}
      {messagesModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <MessageSquare size={18} className="text-indigo-600" />
                <h3 className="font-bold text-foreground">Messages — Delivery #{messagesModal.requestId}</h3>
              </div>
              <button onClick={() => setMessagesModal(null)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                <X size={16} className="text-slate-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messagesModal.messages.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No messages</p>
              ) : (
                messagesModal.messages.map(m => (
                  <div key={m.id} className={`flex flex-col ${m.senderType === "driver" ? "items-end" : "items-start"}`}>
                    <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${m.senderType === "driver" ? "bg-blue-600 text-white" : m.senderType === "customer" ? "bg-slate-100 text-foreground" : "bg-amber-100 text-amber-900 dark:text-amber-300"}`}>
                      {m.content}
                    </div>
                    <span className="text-xs text-slate-400 mt-0.5 capitalize">{m.senderType} · {ago(m.createdAt)}</span>
                  </div>
                ))
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200">
              <button onClick={() => setMessagesModal(null)} className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-xl transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-8">
        <DeliveryRunsControls embedded />
        {/* ── Item Deliveries ─────────────────────────────────────── */}
        <section>
          <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Package size={20} className="text-indigo-600" /> Item Deliveries
          </h2>

          {itemDeliveriesLoading && itemDeliveries.length === 0 && (
            <div className="text-sm text-slate-400 py-4">Loading…</div>
          )}

          {/* Live item deliveries */}
          {(() => {
            const liveItems = itemDeliveries.filter(r => ["dispatching", "accepted", "picked_up"].includes(r.status));
            if (liveItems.length === 0) return null;
            return (
              <div className="mb-6">
                <h3 className="text-sm font-bold text-amber-700 mb-3 uppercase tracking-wide">Live ({liveItems.length})</h3>
                <div className="space-y-2">
                  {liveItems.map(r => (
                    <div key={r.id} className={`bg-white rounded-2xl p-4 border-2 ${ITEM_STATUS_STYLE[r.status]?.border ?? "border-amber-200"}`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-foreground">#{r.id}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ITEM_STATUS_STYLE[r.status]?.badge ?? "bg-amber-100 text-amber-700"}`}>
                            {ITEM_STATUS_LABELS[r.status] ?? r.status}
                          </span>
                          {r.driverName && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Navigation size={11} /> {r.driverName}
                            </span>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-bold text-slate-700">{pence(r.quotedFeePence)}</div>
                          <div className="text-xs text-green-700 font-medium">Driver: {pence(r.driverEarningsPence)}</div>
                          <div className="text-xs text-indigo-700 font-medium">Hub: {pence(r.platformFeePence)}</div>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 flex items-center gap-1 mb-1">
                        <User size={12} /> {r.customerName}
                      </p>
                      <p className="text-xs text-slate-500 flex items-center gap-1 mb-0.5">
                        <MapPin size={11} className="text-green-500" /> {r.pickupAddress}
                      </p>
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <MapPin size={11} className="text-red-500" /> {r.dropoffAddress}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Recent history table */}
          <div>
            <h3 className="text-sm font-bold text-slate-500 mb-3 uppercase tracking-wide">Recent History ({itemDeliveries.length})</h3>
            {itemDeliveries.length === 0 && !itemDeliveriesLoading && (
              <p className="text-sm text-slate-400 py-2">No item deliveries yet.</p>
            )}
            {itemDeliveries.length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">ID</th>
                      <th className="px-3 py-2 text-left font-semibold">Status</th>
                      <th className="px-3 py-2 text-left font-semibold">Customer</th>
                      <th className="px-3 py-2 text-left font-semibold">Pickup</th>
                      <th className="px-3 py-2 text-left font-semibold">Dropoff</th>
                      <th className="px-3 py-2 text-right font-semibold">Customer paid</th>
                      <th className="px-3 py-2 text-right font-semibold">Driver earns</th>
                      <th className="px-3 py-2 text-right font-semibold">Hub fee</th>
                      <th className="px-3 py-2 text-right font-semibold">Tip (driver net)</th>
                      <th className="px-3 py-2 text-center font-semibold">Rating</th>
                      <th className="px-3 py-2 text-left font-semibold">Driver</th>
                      <th className="px-3 py-2 text-left font-semibold">Date</th>
                      <th className="px-3 py-2 text-center font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {itemDeliveries.map(r => (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 font-bold text-foreground">#{r.id}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ITEM_STATUS_STYLE[r.status]?.badge ?? "bg-slate-100 text-slate-600"}`}>
                            {ITEM_STATUS_LABELS[r.status] ?? r.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-700">{r.customerName}</td>
                        <td className="px-3 py-2 text-slate-600 text-xs">{r.pickupAddress}</td>
                        <td className="px-3 py-2 text-slate-600 text-xs">{r.dropoffAddress}</td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-700">{pence(r.quotedFeePence)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-green-700">{pence(r.driverEarningsPence)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-indigo-700">{pence(r.platformFeePence)}</td>
                        <td className="px-3 py-2 text-right text-pink-600 font-medium">
                          {r.tipPence > 0 ? pence(r.tipPence) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-center text-amber-600">
                          {r.rating != null ? `${r.rating}★` : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{r.driverName ?? <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{ago(r.createdAt)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => void loadMessages(r.id)}
                              className="p-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-colors"
                              title="View messages"
                            >
                              <MessageSquare size={13} />
                            </button>
                            {(r.status === "accepted" || r.status === "picked_up") && (
                              <button
                                onClick={() => void handleItemRemind(r.id)}
                                className="p-1.5 rounded-lg bg-sky-50 dark:bg-sky-900/20 hover:bg-sky-100 text-sky-700 transition-colors"
                                title="Remind the driver now"
                              >
                                <Bell size={13} />
                              </button>
                            )}
                            {(r.status === "accepted" || r.status === "picked_up") && (
                              <button
                                onClick={() => void handleItemComplete(r.id)}
                                disabled={itemRedispatchingId === r.id}
                                className="p-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 text-emerald-700 disabled:opacity-50 transition-colors"
                                title="Mark as delivered"
                              >
                                <CheckCheck size={13} />
                              </button>
                            )}
                            {(r.status === "accepted" || r.status === "picked_up") && (
                              <button
                                onClick={() => void handleItemReassign(r.id)}
                                disabled={itemRedispatchingId === r.id}
                                className="p-1.5 rounded-lg bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 text-orange-700 disabled:opacity-50 transition-colors"
                                title="Take off this driver and re-offer"
                              >
                                {itemRedispatchingId === r.id
                                  ? <RotateCw size={13} className="animate-spin" />
                                  : <UserX size={13} />}
                              </button>
                            )}
                            {(r.status === "no_drivers" || r.status === "dispatching") && (
                              <button
                                onClick={() => void handleItemRedispatch(r.id)}
                                disabled={itemRedispatchingId === r.id}
                                className="p-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 text-amber-700 disabled:opacity-50 transition-colors"
                                title="Re-dispatch to drivers"
                              >
                                {itemRedispatchingId === r.id
                                  ? <RotateCw size={13} className="animate-spin" />
                                  : <RotateCw size={13} />
                                }
                              </button>
                            )}
                            {r.status !== "refunded" && r.status !== "cancelled" && (
                              <button
                                onClick={() => void handleItemRefund(r.id, r.customerName, r.quotedFeePence + (r.grossTipPence??r.tipPence))}
                                disabled={refundingId === r.id}
                                className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 disabled:opacity-50 transition-colors"
                                title="Refund"
                              >
                                {refundingId === r.id
                                  ? <RotateCw size={13} className="animate-spin" />
                                  : <RotateCcw size={13} />
                                }
                              </button>
                            )}
                            {r.status === "refunded" && (
                              <span className="text-xs text-red-500 font-semibold px-2 py-0.5 bg-red-50 rounded-full">Refunded</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <hr className="border-slate-200" />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-2xl font-bold text-primary mb-1">Hub Dash Live</h1>
            <p className="text-sm text-slate-500">
              Real-time view of all active Hub Dash orders.
              {lastRefresh && <span className="ml-2 text-slate-400">Refreshed {ago(lastRefresh.toISOString())}</span>}
            </p>
          </div>
          <button
            onClick={() => void load()}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {loading && !data && (
          <div className="text-center py-16 text-slate-400">Loading…</div>
        )}

        {data && total === 0 && (
          <div className="text-center py-16">
            <CheckCircle2 size={40} className="mx-auto text-green-400 mb-3" />
            <p className="text-slate-500 font-medium">No active Hub Dash orders right now</p>
          </div>
        )}

        {/* Dispatching — waiting for driver to accept */}
        {(data?.dispatching.length ?? 0) > 0 && (
          <section>
            <h2 className="text-base font-bold text-amber-700 mb-3 flex items-center gap-2">
              <Clock size={16} /> Finding a driver ({data!.dispatching.length})
            </h2>
            <div className="space-y-3">
              {data!.dispatching.map((order) => {
                const pendingOffers = order.offers.filter((o) => o.status === "pending");
                const pastOffers = order.offers.filter((o) => o.status !== "pending");
                return (
                  <div key={order.orderId} className="bg-white border-2 border-amber-200 rounded-2xl p-5">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <span className="font-bold text-foreground text-base">#{order.orderId}</span>
                        <span className="ml-2 text-slate-500 text-sm">{order.storeName}</span>
                        <span className="ml-3 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold uppercase tracking-wide">
                          {order.dispatchStatus === "offered" ? "Offers sent" : "Searching..."}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-slate-700">{pence(order.totalPence)}</span>
                    </div>
                    {order.customerName && (
                      <p className="text-sm text-slate-600 mb-1 flex items-center gap-1">
                        <User size={12} /> {order.customerName}
                      </p>
                    )}
                    {order.deliveryAddress && (
                      <p className="text-sm text-slate-600 mb-3 flex items-center gap-1">
                        <MapPin size={12} /> {order.deliveryAddress}
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mb-3">Placed {ago(order.createdAt)}</p>

                    {/* Active offers */}
                    {pendingOffers.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Active offers</p>
                        <div className="space-y-1.5">
                          {pendingOffers.map((offer, i) => (
                            <div key={i} className="flex items-center justify-between bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl px-3 py-2 text-sm">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-800">{offer.driverName ?? "Driver"}</span>
                                {offer.distanceKm != null && (
                                  <span className="text-xs text-slate-400">{offer.distanceKm.toFixed(1)} km away</span>
                                )}
                              </div>
                              <span className="font-mono text-amber-700 font-bold text-xs">{timeLeft(offer.expiresAt)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Past offers */}
                    {pastOffers.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Previous offers</p>
                        <div className="flex flex-wrap gap-2">
                          {pastOffers.map((offer, i) => (
                            <span key={i} className={`text-xs px-2 py-0.5 rounded-full border font-medium ${OFFER_STATUS_STYLE[offer.status] ?? "bg-slate-100 text-slate-500"}`}>
                              {offer.driverName ?? "Driver"} · {offer.status}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        onClick={() => void handleRedispatch(order.orderId)}
                        disabled={redispatchingId === order.orderId}
                        className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 disabled:opacity-50 text-amber-700 text-xs font-semibold rounded-xl border border-amber-200 transition-colors"
                      >
                        {redispatchingId === order.orderId
                          ? <span className="w-3 h-3 border-2 border-amber-400/30 border-t-amber-600 rounded-full animate-spin" />
                          : <RotateCcw size={11} />
                        }
                        Re-dispatch
                      </button>
                      <BoostFeeControl
                        orderId={order.orderId}
                        deliveryFeePence={order.deliveryFeePence}
                        fuelSupplementPence={order.driverFuelSupplementPence}
                        onSaved={() => void load()}
                        notify={toast}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* In Transit */}
        {(data?.inTransit.length ?? 0) > 0 && (
          <section>
            <h2 className="text-base font-bold text-blue-700 mb-3 flex items-center gap-2">
              <Truck size={16} /> In transit ({data!.inTransit.length})
            </h2>
            <div className="space-y-3">
              {data!.inTransit.map((order) => (
                <div key={order.orderId} className="bg-white border border-blue-200 rounded-2xl p-5">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <span className="font-bold text-foreground text-base">#{order.orderId}</span>
                      <span className="ml-2 text-slate-500 text-sm">{order.storeName}</span>
                      <span className={`ml-3 text-xs px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${order.status === "out_for_delivery" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                        {order.status === "out_for_delivery" ? "On the way" : order.status.replace(/_/g, " ")}
                      </span>
                    </div>
                    <span className="text-sm font-bold text-slate-700">{pence(order.totalPence)}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <Navigation size={12} className="text-blue-500 shrink-0" />
                    <span className="text-sm font-semibold text-blue-700">{order.driverName ?? "Driver"}</span>
                    {order.driverLat != null && order.driverLng != null && (
                      <span className="text-xs text-slate-400">· GPS live</span>
                    )}
                  </div>
                  {order.customerName && (
                    <p className="text-sm text-slate-600 flex items-center gap-1 mb-1">
                      <User size={12} /> {order.customerName}
                    </p>
                  )}
                  {order.deliveryAddress && (
                    <p className="text-sm text-slate-600 flex items-center gap-1 mb-2">
                      <MapPin size={12} /> {order.deliveryAddress}
                    </p>
                  )}
                  <div className="flex gap-4 text-xs text-slate-400 mb-3">
                    {order.claimedAt && <span>Claimed {ago(order.claimedAt)}</span>}
                    {order.pickedUpAt && <span>· Picked up {ago(order.pickedUpAt)}</span>}
                    {!order.pickedUpAt && order.claimedAt && <span className="text-amber-600">· En route to store</span>}
                  </div>
                  <button
                    onClick={() => void handleRedispatch(order.orderId)}
                    disabled={redispatchingId === order.orderId}
                    className="flex items-center gap-2 px-3 py-1.5 bg-red-50 hover:bg-red-100 disabled:opacity-50 text-red-700 text-xs font-semibold rounded-xl border border-red-200 transition-colors"
                  >
                    {redispatchingId === order.orderId
                      ? <span className="w-3 h-3 border-2 border-red-400/30 border-t-red-600 rounded-full animate-spin" />
                      : <RotateCcw size={11} />
                    }
                    Driver no-show — re-dispatch
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Dispatch failures */}
        {(data?.failures.length ?? 0) > 0 && (
          <section>
            <h2 className="text-base font-bold text-red-700 mb-3 flex items-center gap-2">
              <AlertTriangle size={16} /> Needs attention — no driver found ({data!.failures.length})
            </h2>
            <div className="space-y-3">
              {data!.failures.map((order) => (
                <div key={order.orderId} className="bg-white border-2 border-red-200 rounded-2xl p-5">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <span className="font-bold text-foreground text-base">#{order.orderId}</span>
                      <span className="ml-2 text-slate-500 text-sm">{order.storeName}</span>
                      <span className="ml-3 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold uppercase tracking-wide">Dispatch failed</span>
                    </div>
                    <span className="text-sm font-bold text-slate-700">{pence(order.totalPence)}</span>
                  </div>
                  {order.customerName && (
                    <p className="text-sm text-slate-600 flex items-center gap-1 mb-1">
                      <User size={12} /> {order.customerName}
                    </p>
                  )}
                  {order.deliveryAddress && (
                    <p className="text-sm text-slate-600 flex items-center gap-1 mb-3">
                      <MapPin size={12} /> {order.deliveryAddress}
                    </p>
                  )}
                  <p className="text-xs text-slate-400 mb-3">Placed {ago(order.createdAt)}</p>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={() => void handleRedispatch(order.orderId)}
                      disabled={redispatchingId === order.orderId}
                      className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
                    >
                      {redispatchingId === order.orderId
                        ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        : <><RotateCcw size={13} /> Re-dispatch</>
                      }
                    </button>
                    <BoostFeeControl
                      orderId={order.orderId}
                      deliveryFeePence={order.deliveryFeePence}
                      fuelSupplementPence={order.driverFuelSupplementPence}
                      onSaved={() => void load()}
                      notify={toast}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </AdminLayout>
  );
}
